import { Group, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { BUILD_COMMIT_HASH } from 'env';
import { ReactNode, useEffect, useRef } from 'react';
import { INFO_NOTIFICATION_COLOR } from 'utils/constants';
import P2Regular from 'components/typography/P2Regular';
import { IconRefresh } from '@tabler/icons-react';
import Button from 'components/Button';

const VERSION_CHECK_INTERVAL_MS = 60000;
const VERSION_CHECK_NOTIFICATION_ID = 'new-build-version-available';

interface VersionMetadata {
  buildCommitHash?: string;
}

/**
 * Normalize commit hash for a stable equality check.
 */
function normalizeCommitHash(value: string | null | undefined): string {
  return (value || '').trim().toLowerCase();
}

/**
 * Force a full page reload with a cache-busting query string.
 */
function hardReloadPage() {
  const nextUrl = new URL(window.location.href);

  nextUrl.searchParams.set('t', String(Date.now()));
  window.location.href = nextUrl.toString();
}

/**
 * Show a permanent notification that points user to hard reload.
 */
function showNewVersionNotification() {
  notifications.show({
    id: VERSION_CHECK_NOTIFICATION_ID,
    color: INFO_NOTIFICATION_COLOR,
    title: 'Nová verzia Objekto',
    autoClose: false,
    withCloseButton: false,
    message: (
      <Stack spacing={4}>
        <P2Regular size="sm">
          Je dostupná nová verzia aplikácie Objekto. Aby ste ju začali používať, je potrebné obnoviť stránku.
        </P2Regular>
        <Group>
          <Button
            size="sm"
            variant="subtle"
            leftIcon={<IconRefresh />}
            onClick={(event) => {
              event.preventDefault();
              hardReloadPage();
            }}
          >
            Obnoviť stránku
          </Button>
        </Group>
      </Stack>
    ),
  });
}

/**
 * Periodically checks deployment version and informs user when update is available.
 */
export default function BuildVersionProvider({ children }: { children: ReactNode }) {
  const hasShownNotificationRef = useRef(false);

  useEffect(() => {
    let isUnmounted = false;

    const currentCommitHash = normalizeCommitHash(BUILD_COMMIT_HASH);

    /**
     * Compare bundled hash with the hash served by the server.
     */
    async function checkForNewVersion() {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });

        if (!response.ok) {
          return;
        }

        const metadata = (await response.json()) as VersionMetadata;
        const deployedCommitHash = normalizeCommitHash(metadata.buildCommitHash);

        if (!deployedCommitHash || isUnmounted || hasShownNotificationRef.current) {
          return;
        }

        if (deployedCommitHash !== currentCommitHash) {
          hasShownNotificationRef.current = true;
          showNewVersionNotification();
        }
      } catch {
        // Ignore version check failures and retry on the next interval.
      }
    }

    checkForNewVersion();

    const intervalId = window.setInterval(() => {
      checkForNewVersion();
    }, VERSION_CHECK_INTERVAL_MS);

    return () => {
      isUnmounted = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return <>{children}</>;
}
